/**
 * magicLinkService.ts
 *
 * HR-framework (hr_v1) magic-link tokens. Parallel to the OPTICS
 * `reviewInvitationService.ts` — same token-as-doc-ID pattern, but covers all
 * five action kinds (CLAUDE.md §H.1) and points at hr_v1 target collections.
 *
 * Collection:
 *   magicLinkTokens — one doc per (cycle, recipient, action, target)
 *
 * SECURITY:
 *   - Token IS the secret. Used as the Firestore document ID so reads are a
 *     single `get` (Firestore rule can permit `get` but deny `list`).
 *   - Single-use tokens are consumed in a transaction (read → assert active
 *     → update to 'used'). This is the only safe way to prevent double-submit.
 */

import {
  doc, getDoc, setDoc, updateDoc, runTransaction,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  MagicLinkToken, MagicLinkAction, MagicLinkStatus,
  MagicLinkRecipientRole, MagicLinkTargetCollection,
  CycleMagicLinkExpiry,
} from '../types/magicLinkToken';
import {
  DEFAULT_EXPIRY_DAYS, isReasonableExpiry, isSingleUseAction,
} from '../types/magicLinkToken';
import type {
  Review360Relationship, ReviewerVisibility,
} from '../types/eprFramework';
import {
  DEFAULT_REVIEWER_VISIBILITY, resolveReviewerVisibility,
} from '../types/eprFramework';
import type { ReminderTag } from '../types/magicLink';
import { routes } from '../routes';

const COL = 'magicLinkTokens';

// ─── Token generation (192-bit hex) ─────────────────────────────────────────

export function generateToken(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

// ─── URL builder ────────────────────────────────────────────────────────────

/**
 * Build the public magic-link URL. Single-dispatch route `/m/<token>`.
 * Delegates to routes.publicMagicLinkUrl — single source of truth for
 * the canonical base. Don't recompose the URL from window.location
 * here; tokens generated on an admin tab opened at `*.web.app` would
 * leak that hostname into emails / clipboards. ENGINEERING.md §3e.
 */
export function buildMagicLinkUrl(token: string): string {
  return routes.publicMagicLinkUrl(token);
}

// ─── Expiry resolver ────────────────────────────────────────────────────────

export function resolveExpiryDays(
  action: MagicLinkAction,
  cycleConfig?: CycleMagicLinkExpiry,
  override?: number,
): number {
  if (typeof override === 'number') {
    if (!isReasonableExpiry(override)) {
      throw new Error(`Invalid expiry override: ${override}. Must be 1-90 days.`);
    }
    return override;
  }
  const fromConfig = cycleConfig?.[action];
  if (typeof fromConfig === 'number') {
    if (!isReasonableExpiry(fromConfig)) {
      throw new Error(`Invalid cycle expiry config for ${action}: ${fromConfig}`);
    }
    return fromConfig;
  }
  return DEFAULT_EXPIRY_DAYS[action];
}

// ─── Issue ──────────────────────────────────────────────────────────────────

export interface IssueMagicLinkInput {
  cycleId: string;
  action: MagicLinkAction;

  recipientId: string;
  recipientEmail: string;
  recipientName?: string;
  recipientRole: MagicLinkRecipientRole;

  targetCollection: MagicLinkTargetCollection;
  targetDocId: string;

  /** Override the cycle-default expiry. */
  expiryDays?: number;
  /** Cycle-level expiry configuration. */
  cycleExpiryConfig?: CycleMagicLinkExpiry;

  /**
   * For 'review360_submit' only. Used to snapshot effective visibility on
   * the token doc. Pass the framework template's `reviewerVisibility`
   * override if set; the resolver falls back to DEFAULT_REVIEWER_VISIBILITY.
   */
  templateReviewerVisibility?: Partial<Record<Review360Relationship, ReviewerVisibility>>;

  /**
   * For 'review360_submit' only — who is being reviewed. Rendered on the
   * reviewer's form header. See MagicLinkToken.revieweeId/revieweeName.
   */
  revieweeId?: string;
  revieweeName?: string;
}

export interface IssueMagicLinkResult {
  token: string;
  url: string;
  expiresAt: Date;
}

export async function issueMagicLink(input: IssueMagicLinkInput): Promise<IssueMagicLinkResult> {
  const token = generateToken();
  const expiryDays = resolveExpiryDays(input.action, input.cycleExpiryConfig, input.expiryDays);
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + expiryDays);

  let effectiveVisibility: ReviewerVisibility | undefined;
  if (input.action === 'review360_submit') {
    if (!isReview360Relationship(input.recipientRole)) {
      throw new Error(
        `review360_submit token must have recipientRole in {manager, peer, subordinate}, got ${input.recipientRole}`,
      );
    }
    effectiveVisibility = resolveReviewerVisibility(
      input.recipientRole,
      input.templateReviewerVisibility,
    );
  }

  const data: Omit<MagicLinkToken, 'id'> = {
    cycleId:          input.cycleId,
    action:           input.action,
    recipientId:      input.recipientId,
    recipientEmail:   input.recipientEmail.toLowerCase().trim(),
    recipientName:    input.recipientName,
    recipientRole:    input.recipientRole,
    targetCollection: input.targetCollection,
    targetDocId:      input.targetDocId,
    singleUse:        isSingleUseAction(input.action),
    status:           'active',
    sentAt:           serverTimestamp() as unknown as Timestamp,
    expiresAt:        Timestamp.fromDate(expiresAt),
    remindersSent:    [],
    ...(effectiveVisibility ? { effectiveVisibility } : {}),
    ...(input.action === 'review360_submit' && input.revieweeId
      ? { revieweeId: input.revieweeId, ...(input.revieweeName ? { revieweeName: input.revieweeName } : {}) }
      : {}),
  };

  // Token IS the doc ID — see SECURITY note at top of file.
  await setDoc(doc(db, COL, token), data);

  return { token, url: buildMagicLinkUrl(token), expiresAt };
}

function isReview360Relationship(role: MagicLinkRecipientRole): role is Review360Relationship {
  return role === 'manager' || role === 'peer' || role === 'subordinate';
}

// ─── Verify (read path) ─────────────────────────────────────────────────────

export interface VerifyOptions {
  expectedAction?: MagicLinkAction;
  expectedCycleId?: string;
}

export class MagicLinkError extends Error {
  constructor(public code: MagicLinkErrorCode, message: string) {
    super(message);
    this.name = 'MagicLinkError';
  }
}

export type MagicLinkErrorCode =
  | 'not_found'
  | 'expired'
  | 'used'
  | 'revoked'
  | 'wrong_action'
  | 'wrong_cycle';

/**
 * Fetch and validate a token. Throws MagicLinkError on any failure with a
 * specific code so the caller can render the right UI (expired vs already
 * used vs revoked vs not-found-or-tampered).
 */
export async function verifyToken(
  token: string,
  opts: VerifyOptions = {},
): Promise<MagicLinkToken> {
  const snap = await getDoc(doc(db, COL, token));
  if (!snap.exists()) {
    throw new MagicLinkError('not_found', 'Link is invalid or has been removed.');
  }
  const tok = { id: snap.id, ...(snap.data() as Omit<MagicLinkToken, 'id'>) };

  if (tok.status === 'revoked') {
    throw new MagicLinkError('revoked', `Link has been revoked: ${tok.revokedReason ?? '(no reason given)'}`);
  }
  if (tok.status === 'used' && tok.singleUse) {
    throw new MagicLinkError('used', 'This link has already been used.');
  }
  // expiresAt as Timestamp
  const expDate = tok.expiresAt.toDate();
  if (expDate.getTime() <= Date.now()) {
    // Self-heal status to 'expired' (best-effort, non-blocking)
    updateDoc(doc(db, COL, token), { status: 'expired' as MagicLinkStatus }).catch(() => undefined);
    throw new MagicLinkError('expired', `Link expired on ${expDate.toLocaleString('en-GB')}.`);
  }

  if (opts.expectedAction && tok.action !== opts.expectedAction) {
    throw new MagicLinkError('wrong_action', `This link is not for action ${opts.expectedAction}.`);
  }
  if (opts.expectedCycleId && tok.cycleId !== opts.expectedCycleId) {
    throw new MagicLinkError('wrong_cycle', 'This link is not for the currently open cycle.');
  }

  return tok;
}

// ─── Open & consume ─────────────────────────────────────────────────────────

export async function markTokenOpened(token: string): Promise<void> {
  const ref = doc(db, COL, token);
  // openedAt is a "first observed" marker — only write if not already set.
  // Cheap optimistic update; ignore failure (the field is informational).
  try {
    const snap = await getDoc(ref);
    if (!snap.exists() || (snap.data() as MagicLinkToken).openedAt) return;
    await updateDoc(ref, { openedAt: serverTimestamp() });
  } catch {
    // intentionally swallow — informational only
  }
}

/**
 * Consume a single-use token. Idempotent for already-used tokens (returns
 * silently). Throws if active token cannot be consumed.
 *
 * Uses a transaction to prevent the classic double-submit race.
 */
export async function markTokenUsed(token: string): Promise<void> {
  const ref = doc(db, COL, token);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new MagicLinkError('not_found', 'Token not found.');
    }
    const tok = snap.data() as MagicLinkToken;
    if (!tok.singleUse) {
      // Reusable token (e.g. result_view) — no-op.
      return;
    }
    if (tok.status === 'used') {
      // Idempotent — already consumed. Don't throw.
      return;
    }
    if (tok.status !== 'active') {
      throw new MagicLinkError(tok.status as MagicLinkErrorCode, `Token not active: ${tok.status}.`);
    }
    if (tok.expiresAt.toDate().getTime() <= Date.now()) {
      throw new MagicLinkError('expired', 'Token has expired.');
    }
    tx.update(ref, {
      status: 'used' as MagicLinkStatus,
      usedAt: serverTimestamp(),
    });
  });
}

// ─── Revoke ─────────────────────────────────────────────────────────────────

export async function revokeToken(token: string, reason: string): Promise<void> {
  await updateDoc(doc(db, COL, token), {
    status: 'revoked' as MagicLinkStatus,
    revokedAt: serverTimestamp(),
    revokedReason: reason,
  });
}

// ─── Reminder bookkeeping ───────────────────────────────────────────────────

/**
 * Phase-1 sending is manual (HR copies the URL). This function just records
 * that a reminder was logged for audit purposes. Actual delivery (email/WA)
 * is wired in Phase-2.
 */
export async function recordReminder(token: string, tag: ReminderTag): Promise<void> {
  const ref = doc(db, COL, token);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new MagicLinkError('not_found', 'Token not found.');
  }
  const tok = snap.data() as MagicLinkToken;
  const next = Array.from(new Set([...(tok.remindersSent ?? []), tag])) as ReminderTag[];
  await updateDoc(ref, { remindersSent: next });
}

// ─── Batch issuers (used by cycle launcher in later chunks) ────────────────

export interface BatchIssueResult {
  recipientId: string;
  token: string;
  url: string;
  expiresAt: Date;
}

export interface EPRBatchInput {
  cycleId: string;
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  /** One entry per employee: their submission doc + their own user record. */
  recipients: Array<{
    recipientId: string;
    recipientEmail: string;
    recipientName?: string;
    submissionId: string;
  }>;
}

export async function issueEPRSelfTokens(input: EPRBatchInput): Promise<BatchIssueResult[]> {
  const out: BatchIssueResult[] = [];
  for (const r of input.recipients) {
    const res = await issueMagicLink({
      cycleId: input.cycleId,
      action: 'epr_self_submit',
      recipientId: r.recipientId,
      recipientEmail: r.recipientEmail,
      recipientName: r.recipientName,
      recipientRole: 'employee',
      targetCollection: 'eprSubmissions',
      targetDocId: r.submissionId,
      cycleExpiryConfig: input.cycleExpiryConfig,
    });
    out.push({ recipientId: r.recipientId, ...res });
  }
  return out;
}

export interface EPRManagerBatchInput {
  cycleId: string;
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  /** One entry per (manager → their direct report's submission). */
  recipients: Array<{
    managerId: string;
    managerEmail: string;
    managerName?: string;
    submissionId: string;
  }>;
}

export async function issueEPRManagerTokens(input: EPRManagerBatchInput): Promise<BatchIssueResult[]> {
  const out: BatchIssueResult[] = [];
  for (const r of input.recipients) {
    const res = await issueMagicLink({
      cycleId: input.cycleId,
      action: 'epr_manager_submit',
      recipientId: r.managerId,
      recipientEmail: r.managerEmail,
      recipientName: r.managerName,
      recipientRole: 'manager',
      targetCollection: 'eprSubmissions',
      targetDocId: r.submissionId,
      cycleExpiryConfig: input.cycleExpiryConfig,
    });
    out.push({ recipientId: r.managerId, ...res });
  }
  return out;
}

export interface Review360PanelEntry {
  reviewerId: string;
  reviewerEmail: string;
  reviewerName?: string;
  relationship: Review360Relationship;
  /** The submission doc that this reviewer will be writing into. */
  submissionId: string;
  /** Who is being reviewed — snapshotted onto the token for the form header. */
  revieweeId?: string;
  revieweeName?: string;
}

export interface Review360BatchInput {
  cycleId: string;
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  templateReviewerVisibility?: Partial<Record<Review360Relationship, ReviewerVisibility>>;
  panel: Review360PanelEntry[];
}

export async function issue360ReviewerTokens(input: Review360BatchInput): Promise<BatchIssueResult[]> {
  const out: BatchIssueResult[] = [];
  for (const r of input.panel) {
    const res = await issueMagicLink({
      cycleId: input.cycleId,
      action: 'review360_submit',
      recipientId: r.reviewerId,
      recipientEmail: r.reviewerEmail,
      recipientName: r.reviewerName,
      recipientRole: r.relationship,
      targetCollection: 'review360Submissions',
      targetDocId: r.submissionId,
      cycleExpiryConfig: input.cycleExpiryConfig,
      templateReviewerVisibility: input.templateReviewerVisibility,
      revieweeId: r.revieweeId,
      revieweeName: r.revieweeName,
    });
    out.push({ recipientId: r.reviewerId, ...res });
  }
  return out;
}

export interface AppraisalSignInput {
  cycleId: string;
  appraisalId: string;
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  employee: { id: string; email: string; name?: string };
  manager: { id: string; email: string; name?: string };
}

export async function issueAppraisalSignTokens(input: AppraisalSignInput): Promise<{
  employee: BatchIssueResult;
  manager: BatchIssueResult;
}> {
  const emp = await issueMagicLink({
    cycleId: input.cycleId,
    action: 'appraisal_sign',
    recipientId: input.employee.id,
    recipientEmail: input.employee.email,
    recipientName: input.employee.name,
    recipientRole: 'employee',
    targetCollection: 'appraisalForms',
    targetDocId: input.appraisalId,
    cycleExpiryConfig: input.cycleExpiryConfig,
  });
  const mgr = await issueMagicLink({
    cycleId: input.cycleId,
    action: 'appraisal_sign',
    recipientId: input.manager.id,
    recipientEmail: input.manager.email,
    recipientName: input.manager.name,
    recipientRole: 'manager',
    targetCollection: 'appraisalForms',
    targetDocId: input.appraisalId,
    cycleExpiryConfig: input.cycleExpiryConfig,
  });
  return {
    employee: { recipientId: input.employee.id, ...emp },
    manager:  { recipientId: input.manager.id,  ...mgr },
  };
}

export interface ResultViewBatchInput {
  cycleId: string;
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  /** One token per (cycle, employee, recipient) per decision #3. */
  recipients: Array<{
    recipientId: string;
    recipientEmail: string;
    recipientName?: string;
    recipientRole: 'employee' | 'manager';
    appraisalId: string;
  }>;
}

export async function issueResultViewTokens(input: ResultViewBatchInput): Promise<BatchIssueResult[]> {
  const out: BatchIssueResult[] = [];
  for (const r of input.recipients) {
    const res = await issueMagicLink({
      cycleId: input.cycleId,
      action: 'result_view',
      recipientId: r.recipientId,
      recipientEmail: r.recipientEmail,
      recipientName: r.recipientName,
      recipientRole: r.recipientRole,
      targetCollection: 'appraisalForms',
      targetDocId: r.appraisalId,
      cycleExpiryConfig: input.cycleExpiryConfig,
    });
    out.push({ recipientId: r.recipientId, ...res });
  }
  return out;
}

// ─── Re-exports for convenience ─────────────────────────────────────────────

export {
  DEFAULT_EXPIRY_DAYS,
  DEFAULT_REVIEWER_VISIBILITY,
  isSingleUseAction,
  isReasonableExpiry,
};
export type {
  CycleMagicLinkExpiry, MagicLinkAction, MagicLinkRecipientRole,
  MagicLinkStatus, MagicLinkTargetCollection,
};
